import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphConfig, securityGroups, roles } from '../authConfig';
import { usersApi, authApi } from '../api/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const checkUserRole = async () => {
      // Don't process if MSAL is still initializing
      if (inProgress !== 'none') {
        return;
      }

      if (accounts.length > 0) {
        const account = accounts[0];
        setUserName(account.name || account.username);

        // Check if we're in development mode (placeholder group IDs)
        const isDevelopmentMode =
          securityGroups.adminGroupId === 'your-admin-group-id' ||
          securityGroups.userGroupId === 'your-user-group-id';

        try {
          let userProfile = null;
          let userGroups = [];
          let determinedRole = isDevelopmentMode ? roles.ADMIN : roles.USER;
          let azureAccessToken = null; // Store Azure AD token for backend verification

          if (isDevelopmentMode) {
            console.warn('🚨 DEVELOPMENT MODE: Azure AD groups not configured, granting admin access');
            console.warn('⚠️  Configure REACT_APP_ADMIN_GROUP_ID and REACT_APP_USER_GROUP_ID for production');

            // In development mode, use basic profile only
            try {
              const basicResponse = await instance.acquireTokenSilent({
                scopes: ['User.Read'],
                account: account,
              });

              azureAccessToken = basicResponse.accessToken; // Store token

              const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
                headers: {
                  Authorization: `Bearer ${basicResponse.accessToken}`,
                },
              });

              if (profileResponse.ok) {
                userProfile = await profileResponse.json();
              }
            } catch (profileError) {
              console.error('Could not fetch profile:', profileError);
              // Continue with minimal profile
              userProfile = {
                mail: account.username,
                displayName: account.name || account.username,
              };
            }

            determinedRole = roles.ADMIN; // Grant admin in dev mode
          } else {
            // Production mode: require proper group membership
            try {
              // Try to get access token with Group.Read.All permission
              const response = await instance.acquireTokenSilent({
                scopes: ['User.Read', 'GroupMember.Read.All'],
                account: account,
              });

              azureAccessToken = response.accessToken; // Store token

              // Fetch user profile from Microsoft Graph
              const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
                headers: {
                  Authorization: `Bearer ${response.accessToken}`,
                },
              });

              if (profileResponse.ok) {
                userProfile = await profileResponse.json();
              }

              // Fetch user's groups from Microsoft Graph
              const groupsResponse = await fetch(graphConfig.graphGroupsEndpoint, {
                headers: {
                  Authorization: `Bearer ${response.accessToken}`,
                },
              });

              if (groupsResponse.ok) {
                const groupsData = await groupsResponse.json();
                userGroups = groupsData.value.map((group) => group.id);

                console.log('✅ Successfully fetched user groups');
                console.log('User groups:', userGroups);
                console.log('Looking for admin group:', securityGroups.adminGroupId); //b60b75a2-1ec2-4daf-99dd-5e4a837e66ff,ce800b75-e735-469a-8923-7aa63b06abb7
                console.log('Looking for user group:', securityGroups.userGroupId); //052faf91-cbcf-4b7c-8adc-8f3db799c725,b386b9ff-775b-4521-ae2e-5cceed02c414

                let isAdmin = false;
                let isUser = false;
                userGroups.forEach((userGroup) => {
                  if (securityGroups.adminGroupId.split(',').includes(userGroup)) {
                    isAdmin = true;
                  }
                  if (securityGroups.userGroupId.split(',').includes(userGroup)) {
                    isUser = true;
                  }
                });

                // Check if user is in admin or user group
                if (isAdmin) {
                  determinedRole = roles.ADMIN;
                  console.log('✅ User is in ADMIN group');
                } else if (isUser) {
                  determinedRole = roles.USER;
                  console.log('✅ User is in USER group');
                } else {
                  console.error('❌ User not in any configured group');
                  console.error('User groups:', userGroups);
                  console.error('Admin group ID:', securityGroups.adminGroupId);
                  console.error('User group ID:', securityGroups.userGroupId);
                  console.error('Access denied');
                  setUserRole(null);
                  setLoading(false);
                  return; // User not authorized
                }
              } else {
                console.error('Could not fetch user groups, access denied');
                setUserRole(null);
                setLoading(false);
                return; // Cannot verify group membership, deny access
              }
            } catch (groupError) {
              console.warn('Could not fetch groups (consent may be required):', groupError.message);

              // Check if this is a consent error
              if (groupError.message && groupError.message.includes('AADSTS65001')) {
                console.log('🔄 Consent required - initiating interactive consent flow...');

                // Try to acquire token interactively to get consent
                try {
                  await instance.acquireTokenPopup({
                    scopes: ['User.Read', 'GroupMember.Read.All'],
                    account: account,
                  });

                  // After consent, reload the page to start fresh
                  console.log('✅ Consent granted! Reloading page...');
                  window.location.reload();
                  return;
                } catch (consentError) {
                  console.error('❌ User denied consent or consent failed:', consentError);
                  setUserRole(null);
                  setLoading(false);
                  return;
                }
              }

              // Fallback to basic User.Read permission only
              try {
                const basicResponse = await instance.acquireTokenSilent({
                  scopes: ['User.Read'],
                  account: account,
                });

                const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
                  headers: {
                    Authorization: `Bearer ${basicResponse.accessToken}`,
                  },
                });

                if (profileResponse.ok) {
                  userProfile = await profileResponse.json();
                }
              } catch (basicError) {
                console.error('Could not fetch basic profile:', basicError);
              }

              // Without group membership in production, deny access
              console.error('Cannot verify group membership in production mode, access denied');
              setUserRole(null);
              setLoading(false);
              return;
            }
          }

          // Sync user profile to backend
          try {
            const syncData = {
              entraId: account.localAccountId || account.homeAccountId,
              email: userProfile?.mail || account.username,
              displayName: userProfile?.displayName || account.name || account.username,
              givenName: userProfile?.givenName || '',
              surname: userProfile?.surname || '',
              jobTitle: userProfile?.jobTitle || '',
              department: userProfile?.department || '',
              officeLocation: userProfile?.officeLocation || '',
              mobilePhone: userProfile?.mobilePhone || '',
              businessPhones: userProfile?.businessPhones || [],
              role: determinedRole,
              entraGroups: userGroups,
            };

            // Pass Azure AD token for backend verification
            await usersApi.syncUser(syncData, azureAccessToken);

            // Get JWT token from backend (also pass Azure AD token for verification)
            const loginResponse = await authApi.login(
              syncData.entraId,
              syncData.email,
              azureAccessToken
            );

            // Store JWT token in localStorage
            localStorage.setItem('jwt_token', loginResponse.data.access_token);

            // Set role from backend response (backend is source of truth)
            setUserRole(loginResponse.data.user.role);
          } catch (syncError) {
            console.error('Error syncing user profile or getting token:', syncError);

            // In development mode, continue anyway with warning
            if (isDevelopmentMode) {
              console.warn('⚠️  Sync failed in dev mode, continuing anyway');
              setUserRole(determinedRole);
            } else {
              // In production, deny access if sync fails
              setUserRole(null);
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking user role:', error);

          // In development mode, grant access anyway
          if (isDevelopmentMode) {
            console.warn('⚠️  Error in dev mode, granting admin access anyway');
            setUserRole(roles.ADMIN);
          } else {
            // Fail closed in production - deny access on error
            setUserRole(null);
          }
        }
      } else {
        setUserRole(null);
        setUserName('');
        // Clear JWT token on logout
        localStorage.removeItem('jwt_token');
      }
      setLoading(false);
    };

    checkUserRole();
  }, [accounts, instance, inProgress]);

  const isAdmin = () => userRole === roles.ADMIN;
  const isUser = () => userRole === roles.USER || userRole === roles.ADMIN;
  const isAuthenticated = () => accounts.length > 0 && userRole !== null;

  const value = {
    userRole,
    userName,
    isAdmin,
    isUser,
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
